/**
 * AdminPanel — gestión básica de usuarios y roles del tenant (US-26).
 *
 * Sólo se llega aquí si el descriptor habilita el ítem de menú `admin`
 * (capabilities.menu, R-PT-02). Recordatorio R-PT-04: ocultar el panel es
 * cosmético; el kernel revalida el permiso admin en CADA endpoint. Si un
 * no-admin fuerza la llamada, el backend responde 403 y aquí se muestra el
 * estado de permiso denegado.
 *
 * Funciones (contra mock o kernel real, según VITE_USE_MOCK):
 *  - Listar empleados del tenant (nombre, email, rol, estado invitación).
 *  - Invitar empleado por email eligiendo rol inicial.
 *  - Cambiar el rol de un empleado existente.
 *
 * Aspecto: mismo lenguaje «Despacho» que Saber (GEN-UR-001 t6): cabecera con degradado
 * (`dk-cab`, con cifras), secciones con su título (`dk-mapa__cab`) y tarjetas (`dk-tarjeta`).
 * Solo tokens `--dk-*`; la funcionalidad no cambia.
 */

import { useState, useEffect, useCallback } from 'react'
import { useSession } from '../context/SessionContext.jsx'
import { listUsers, inviteUser, changeUserRole } from '../api/client.js'
import { ROLES, ROLE_LABELS, ESTADO_LABELS } from '../lib/contract.js'
import { messageForCode } from '../api/errors.js'
import { logEvent, logError } from '../lib/log.js'
import { inicialesDe } from '../lib/empleados.js'
import { Avatar } from '../despacho/ui.jsx'
import Cifras from '../despacho/entrada/Cifras.jsx'

function EstadoBadge({ estado }) {
  return (
    <span className={`dk-admin__chip dk-admin__chip--${estado}`}>
      {ESTADO_LABELS[estado] ?? estado}
    </span>
  )
}

function InviteForm({ onInvited }) {
  const [nombre, setNombre]   = useState('')
  const [email, setEmail]     = useState('')
  const [rol, setRol]         = useState('usuario')
  const [sending, setSending] = useState(false)
  const [error, setError]     = useState(null)
  const [ok, setOk]           = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    setOk(null)
    setSending(true)
    try {
      const { user } = await inviteUser({ nombre: nombre.trim(), email: email.trim(), rol })
      logEvent('admin.invite.ok', { email: user.email, rol: user.rol })
      setOk(`Invitación enviada a ${user.email} (${ROLE_LABELS[user.rol]}). Estado: pendiente.`)
      setNombre('')
      setEmail('')
      setRol('usuario')
      onInvited(user)
    } catch (err) {
      logError('admin.invite.error', { code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setSending(false)
    }
  }

  return (
    <form className="dk-admin__sec" onSubmit={submit} aria-label="Invitar empleado">
      <div className="dk-mapa__cab"><h2>Invitar empleado</h2></div>
      <div className="dk-tarjeta">
        <div className="dk-admin__invitar">
        <input
          type="text"
          className="dk-admin__campo"
          placeholder="Nombre"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          required
          disabled={sending}
          aria-label="Nombre del empleado"
        />
        <input
          type="email"
          className="dk-admin__campo dk-admin__campo--email"
          placeholder="email@empresa.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={sending}
          aria-label="Email del empleado"
        />
        <select
          className="dk-admin__campo"
          value={rol}
          onChange={e => setRol(e.target.value)}
          disabled={sending}
          aria-label="Rol inicial"
        >
          {ROLES.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        <button className="dk-btn dk-btn--primario" type="submit" disabled={sending || !nombre.trim() || !email.trim()}>
          {sending ? 'Enviando…' : 'Invitar'}
        </button>
        </div>
        {error && <p className="dk-modal__error dk-admin__msg" role="alert">{error}</p>}
        {ok    && <p className="dk-admin__ok dk-admin__msg" role="status">{ok}</p>}
      </div>
    </form>
  )
}

function UserRow({ user, isSelf, onRoleChanged }) {
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)
  const [saved, setSaved]   = useState(false)

  const onChange = async (e) => {
    const nuevoRol = e.target.value
    const rolPrevio = user.rol
    if (nuevoRol === rolPrevio) return
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const { user: actualizado } = await changeUserRole(user.id, nuevoRol)
      logEvent('admin.role_change.ok', { user_id: user.id, from: rolPrevio, to: actualizado.rol })
      onRoleChanged(actualizado)
      setSaved(true)
    } catch (err) {
      logError('admin.role_change.error', { user_id: user.id, code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <tr className={saving ? 'dk-admin__fila--guardando' : undefined}>
      <td>
        <span className="dk-admin__persona">
          <Avatar iniciales={inicialesDe(user.display_name)} nombre={user.display_name} tamano={32} />
          <span className="dk-admin__nombre">{user.display_name}</span>
        </span>
      </td>
      <td className="dk-admin__muted">{user.email}</td>
      <td>
        <select
          className="dk-admin__campo dk-admin__campo--rol"
          value={user.rol}
          onChange={onChange}
          disabled={saving || isSelf || user.estado === 'baja'}
          title={isSelf ? 'No puedes cambiar tu propio rol' : undefined}
          aria-label={`Rol de ${user.display_name}`}
        >
          {ROLES.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        {saving && <span className="dk-admin__nota">Guardando…</span>}
        {saved  && !saving && <span className="dk-admin__nota dk-admin__nota--ok">Guardado</span>}
        {error  && <span className="dk-admin__nota dk-admin__nota--error" role="alert">{error}</span>}
      </td>
      <td><EstadoBadge estado={user.estado} /></td>
    </tr>
  )
}

export default function AdminPanel() {
  const { user } = useSession()
  const [users, setUsers]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { users } = await listUsers()
      logEvent('admin.list.ok', { count: users.length })
      setUsers(users)
    } catch (err) {
      logError('admin.list.error', { code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleInvited = useCallback((nuevo) => {
    setUsers(prev => [...prev, nuevo])
  }, [])

  const handleRoleChanged = useCallback((actualizado) => {
    setUsers(prev => prev.map(u => (u.id === actualizado.id ? { ...u, ...actualizado } : u)))
  }, [])

  const admins = users.filter(u => u.rol === 'admin' && u.estado !== 'baja').length
  const pendientes = users.filter(u => u.estado === 'pendiente').length
  const cifras = [
    { id: 'personas', valor: users.length, etiqueta: users.length === 1 ? 'persona' : 'personas' },
    { id: 'admins', valor: admins, etiqueta: admins === 1 ? 'administrador' : 'administradores' },
    { id: 'pendientes', valor: pendientes, etiqueta: pendientes === 1 ? 'invitación pendiente' : 'invitaciones pendientes' },
  ]

  return (
    <section className="dk-admin">
      <header className="dk-cab" aria-label="Cabecera de administración">
        <div className="dk-cab__texto">
          <div className="dk-cab__eyebrow">Administración · usuarios</div>
          <h1 className="dk-cab__h1">Administración de usuarios</h1>
          <p className="dk-cab__p">Empleados del tenant, sus roles y el estado de su invitación.</p>
        </div>
        {!loading && !error && <Cifras items={cifras} etiqueta="Cifras de usuarios" />}
      </header>

      <InviteForm onInvited={handleInvited} />

      {loading && <p className="dk-admin__estado">Cargando empleados…</p>}

      {error && !loading && (
        <div className="dk-admin__estado dk-admin__estado--error" role="alert">
          <p>{error}</p>
          <button className="dk-btn" onClick={load}>Reintentar</button>
        </div>
      )}

      {!loading && !error && (
        users.length === 0 ? (
          <p className="dk-admin__estado">Todavía no hay empleados en este tenant.</p>
        ) : (
          <div className="dk-admin__sec">
            <div className="dk-mapa__cab">
              <h2>Empleados</h2>
              <span className="dk-mapa__n">{users.length}</span>
            </div>
            <div className="dk-tarjeta dk-admin__lista">
              <table className="dk-admin__tabla">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <UserRow
                      key={u.id}
                      user={u}
                      isSelf={u.id === user?.id}
                      onRoleChanged={handleRoleChanged}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </section>
  )
}
